import { useEffect } from "react";
import { chatSettings } from "./chat.settings.mjs";

// Pair AI's live chat. The script is public/pair-chat.js, as Pair supplied it,
// so the apps' account screen can load the same file. A <script> written into
// a page's JSX never runs, so this adds it when the page opens instead.
// Loaded once: a page that opens again does not add a second chat button.
//
// Only the pages that render <LiveChat /> (My Account and Contact us, the
// apps' contact page /app/contactus included) can show the chat, and only
// where the engine's settings allow it (chat.settings.mjs). The site moves
// between pages without reloading, so Pair's button and panel would otherwise
// stay on every page after those, payment included. Leaving such a page closes
// the chat and hides it; coming back shows it again, without loading the
// script twice.
// app-chat.html (the apps' account screen) does not use this component.
export const CHAT_SCRIPT_ID = "pair-chat-script";
export const CHAT_SCRIPT_SRC = "/pair-chat.js";
export const CHAT_HIDDEN_STYLE_ID = "pair-chat-hidden";

// What Pair's SDK draws into the page: the button, the preview message beside
// it, the open chat, and the chat's frame.
export const PAIR_ELEMENT_IDS = [
  "pair-ai-bubble-holder",
  "pair-ai-preview-holder",
  "pair-ai-widget-holder",
  "pair-ai-widget-iframe",
];

// A style rule rather than each element's own style, so that a button Pair
// draws only after the customer has already left is hidden too.
function hideChat() {
  const sdk = window.PairAiWidgetSDK;
  if (sdk && typeof sdk.close === "function") {
    try {
      sdk.close();
    } catch (e) {
      // The chat is hidden below whether or not Pair could close it.
    }
  }
  if (document.getElementById(CHAT_HIDDEN_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = CHAT_HIDDEN_STYLE_ID;
  style.textContent =
    PAIR_ELEMENT_IDS.map((id) => "#" + id).join(", ") + " { display: none !important; }";
  document.head.appendChild(style);
}

function showChat() {
  document.getElementById(CHAT_HIDDEN_STYLE_ID)?.remove();
}

// surface: "website" (Contact and My Account) or "apps" (the apps' contact
// page). The engine's settings say whether the chat may show there
// (chat.settings.mjs); until they arrive, nothing is loaded.
export default function LiveChat({ surface = "website" }) {
  useEffect(() => {
    let left = false;

    chatSettings().then((settings) => {
      if (left || !settings[surface]) return;
      showChat();
      if (!document.getElementById(CHAT_SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = CHAT_SCRIPT_ID;
        script.src = CHAT_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
    });

    return () => {
      left = true;
      hideChat();
    };
  }, [surface]);

  return null;
}
